import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { apiaryService, hiveService, inspectionService } from '../services/firebase';
import { Apiary, Hive, InspectionRecord } from '../types';
import { HiveSelection } from '../features/hives/HiveSelection';
import EnhancedInspectionForm from './EnhancedInspectionForm';
import EnhancedInspectionReport from './EnhancedInspectionReport';
import './EnhancedInspectionsPage.css';

interface InspectionStats {
  totalInspections: number;
  thisMonth: number;
  avgFrequency: number;
  healthScore: number;
  dueCount: number;
}

const EnhancedInspectionsPage: React.FC = () => {
  const { user } = useAuth();
  const [inspections, setInspections] = useState<InspectionRecord[]>([]);
  const [apiaries, setApiaries] = useState<Apiary[]>([]);
  const [hives, setHives] = useState<Hive[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<InspectionStats>({
    totalInspections: 0,
    thisMonth: 0,
    avgFrequency: 14,
    healthScore: 94,
    dueCount: 0
  });

  // Enhanced inspection workflow state
  const [showHiveSelection, setShowHiveSelection] = useState(false);
  const [showInspectionForm, setShowInspectionForm] = useState(false);
  const [showInspectionReport, setShowInspectionReport] = useState(false);
  const [selectedApiary, setSelectedApiary] = useState<Apiary | null>(null);
  const [selectedHive, setSelectedHive] = useState<Hive | null>(null);
  const [editingInspection, setEditingInspection] = useState<InspectionRecord | null>(null);
  // Note: editingInspection will be used when edit functionality is fully implemented
  console.log('Edit state:', editingInspection?.id || 'none'); // Temporary to avoid unused variable warning
  const [viewingInspection, setViewingInspection] = useState<InspectionRecord | null>(null);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedApiaryFilter, setSelectedApiaryFilter] = useState('');
  const [selectedHiveFilter, setSelectedHiveFilter] = useState('');
  const [selectedDateRange, setSelectedDateRange] = useState('all');

  // Load all data
  const loadData = async () => {
    if (!user) return;

    try {
      setLoading(true);
      console.log('📊 Loading data for enhanced inspections page...');

      const [apiariesData, hivesData, inspectionsData] = await Promise.all([
        apiaryService.getUserApiaries(user.uid),
        hiveService.getUserHives(user.uid),
        inspectionService.getUserRecentInspections(user.uid, 1000) // Get up to 1000 inspections
      ]);

      setApiaries(apiariesData);
      setHives(hivesData);
      setInspections(inspectionsData);

      console.log(`Loaded ${apiariesData.length} apiaries for enhanced inspections page`);
      console.log(`Loaded ${hivesData.length} hives for enhanced inspections page`);
      console.log(`Loaded ${inspectionsData.length} inspections`);

      // Calculate stats
      const now = new Date();
      const thisMonth = inspectionsData.filter((inspection: InspectionRecord) => {
        const inspectionDate = new Date(inspection.date);
        return inspectionDate.getMonth() === now.getMonth() && 
               inspectionDate.getFullYear() === now.getFullYear();
      }).length;

      setStats({
        totalInspections: inspectionsData.length,
        thisMonth,
        avgFrequency: 14,
        healthScore: 94,
        dueCount: 0
      });

    } catch (error) {
      console.error('Error loading enhanced inspections data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [user]);

  // Helper functions
  const getHiveName = (hiveId: string) => {
    const hive = hives.find(h => h.id === hiveId);
    return hive ? hive.name : 'Unknown Hive';
  };

  const getApiaryName = (hiveId: string) => {
    const hive = hives.find(h => h.id === hiveId);
    if (!hive) return 'Unknown Apiary';
    const apiary = apiaries.find(a => a.id === hive.apiary_id);
    return apiary ? apiary.name : 'Unknown Apiary';
  };

  // Filter inspections
  const getFilteredInspections = () => {
    let filtered = [...inspections];

    if (searchTerm) {
      filtered = filtered.filter(inspection => 
        getHiveName(inspection.hive_id).toLowerCase().includes(searchTerm.toLowerCase()) ||
        getApiaryName(inspection.hive_id).toLowerCase().includes(searchTerm.toLowerCase()) ||
        inspection.notes?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedApiaryFilter) {
      filtered = filtered.filter(inspection => {
        const hive = hives.find(h => h.id === inspection.hive_id);
        return hive?.apiary_id === selectedApiaryFilter;
      });
    }

    if (selectedHiveFilter) {
      filtered = filtered.filter(inspection => inspection.hive_id === selectedHiveFilter);
    }

    if (selectedDateRange !== 'all') {
      const now = new Date();
      const cutoffDate = new Date();
      
      switch (selectedDateRange) {
        case 'week':
          cutoffDate.setDate(now.getDate() - 7);
          break;
        case 'month':
          cutoffDate.setMonth(now.getMonth() - 1);
          break;
        case 'quarter':
          cutoffDate.setMonth(now.getMonth() - 3);
          break;
      }
      
      filtered = filtered.filter(inspection => new Date(inspection.date) >= cutoffDate);
    }

    // Sort by date (newest first)
    filtered.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    return filtered;
  };

  const getInspectionMetrics = (inspection: InspectionRecord) => {
    const metrics = [
      {
        label: 'Population',
        status: inspection.health_rating && inspection.health_rating >= 4 ? 'good' : 'warning',
        icon: '✓'
      },
      {
        label: 'Brood',
        status: inspection.queen_sighted ? 'good' : 'warning',
        icon: inspection.queen_sighted ? '✓' : '!'
      },
      {
        label: 'Health',
        status: inspection.varroa_check ? 'good' : 'warning',
        icon: inspection.varroa_check ? '✓' : '!'
      },
      {
        label: 'Stores',
        status: inspection.health_rating && inspection.health_rating >= 3 ? 'good' : 'bad',
        icon: inspection.health_rating && inspection.health_rating >= 3 ? '✓' : '✗'
      }
    ];

    return metrics;
  };

  const handleStartNewInspection = () => {
    console.log('🔍 Start New Inspection clicked');
    console.log('📊 Apiaries:', apiaries.length, apiaries);
    console.log('🏠 Hives:', hives.length, hives);

    if (apiaries.length === 0) {
      alert('Please create an apiary first before starting an inspection.');
      return;
    }

    if (hives.length === 0) {
      alert('Please add hives to your apiaries before starting an inspection.');
      return;
    }

    if (apiaries.length === 1 && hives.length === 1) {
      console.log('🎯 Single hive setup, going directly to inspection');
      setSelectedApiary(apiaries[0]);
      setSelectedHive(hives[0]);
      setShowInspectionForm(true);
    } else {
      console.log('🔍 Multiple apiaries found, showing hive selection');
      setShowHiveSelection(true);
    }
  };

  const handleInspectApiary = (apiary: Apiary) => {
    console.log('🔍 Inspect Apiary clicked for:', apiary.name);
    const apiaryHives = hives.filter(h => h.apiary_id === apiary.id);
    
    if (apiaryHives.length === 0) {
      alert('No hives found in this apiary. Please add hives first.');
      return;
    }

    if (apiaryHives.length === 1) {
      setSelectedApiary(apiary);
      setSelectedHive(apiaryHives[0]);
      setShowInspectionForm(true);
    } else {
      setSelectedApiary(apiary);
      setShowHiveSelection(true);
    }
  };

  const handleHiveSelect = (hive: Hive) => {
    console.log('🏠 Hive selected:', hive.name);
    setSelectedHive(hive);
    setShowHiveSelection(false);
    setShowInspectionForm(true);
  };

  const handleCloseHiveSelection = () => {
    console.log('❌ Closing hive selection');
    setShowHiveSelection(false);
    setSelectedApiary(null);
  };

  const handleCloseInspectionForm = () => {
    console.log('❌ Closing inspection form');
    setShowInspectionForm(false);
    setSelectedApiary(null);
    setSelectedHive(null);
    setEditingInspection(null);
    loadData(); // Reload data after form closes
  };

  const handleCloseInspectionReport = () => {
    console.log('❌ Closing inspection report');
    setShowInspectionReport(false);
    setSelectedApiary(null);
    setSelectedHive(null);
    setViewingInspection(null);
  };

  const handleEditInspection = async (inspection: InspectionRecord) => {
    console.log('✏️ Edit inspection clicked for:', inspection.id);
    try {
      const hive = hives.find(h => h.id === inspection.hive_id);
      console.log('🏠 Found hive:', hive?.name);
      if (hive) {
        const apiary = apiaries.find(a => a.id === hive.apiary_id);
        console.log('📍 Found apiary:', apiary?.name);
        if (apiary) {
          setEditingInspection(inspection);
          setSelectedApiary(apiary);
          setSelectedHive(hive);
          setShowInspectionForm(true);
          console.log('✅ Opening inspection form for editing');
        } else {
          console.error('❌ Could not find apiary for this inspection');
          alert('Could not find apiary for this inspection');
        }
      } else {
        console.error('❌ Could not find hive for this inspection');
        alert('Could not find hive for this inspection');
      }
    } catch (error) {
      console.error('❌ Error setting up inspection edit:', error);
      alert('Error setting up inspection edit');
    }
  };

  const handleViewInspectionDetails = async (inspection: InspectionRecord) => {
    console.log('👁️ View details clicked for inspection:', inspection.id);
    try {
      const hive = hives.find(h => h.id === inspection.hive_id);
      console.log('🏠 Found hive:', hive?.name);
      if (hive) {
        const apiary = apiaries.find(a => a.id === hive.apiary_id);
        console.log('📍 Found apiary:', apiary?.name);
        if (apiary) {
          setViewingInspection(inspection);
          setSelectedApiary(apiary);
          setSelectedHive(hive);
          setShowInspectionReport(true);
          console.log('✅ Opening detailed inspection report');
        } else {
          console.error('❌ Could not find apiary for this inspection');
          alert('Could not find apiary for this inspection');
        }
      } else {
        console.error('❌ Could not find hive for this inspection');
        alert('Could not find hive for this inspection');
      }
    } catch (error) {
      console.error('❌ Error setting up inspection view:', error);
      alert('Error setting up inspection view');
    }
  };

  // Helper function to transform inspection data for the detailed report
  const transformInspectionForReport = (inspection: InspectionRecord, hive: Hive, apiary: Apiary) => {
    console.log('🔄 Transforming inspection data for report:', {
      inspectionId: inspection.id,
      hiveName: hive.name,
      apiaryName: apiary.name
    });

    // Parse weather data
    const weatherMatch = inspection.weather?.match(/^(.+?),?\s*(\d+)°C$/);
    const weather = weatherMatch ? {
      condition: weatherMatch[1].trim(),
      temperature: parseInt(weatherMatch[2]),
      icon: '🌤️'
    } : undefined;

    // Create badges based on inspection data
    const badges = [
      {
        label: 'Queen Status',
        value: inspection.queen_sighted ? 'Present' : 'Not Sighted',
        status: (inspection.queen_sighted ? 'excellent' : 'danger') as 'excellent' | 'good' | 'warning' | 'danger' | 'neutral',
        icon: '👑',
        subtitle: inspection.queen_sighted ? 'Queen spotted during inspection' : 'Queen not observed'
      },
      {
        label: 'Health Rating',
        value: inspection.health_rating ? `${inspection.health_rating}/5` : 'Not Rated',
        status: ((inspection.health_rating || 0) >= 4 ? 'excellent' : 
                (inspection.health_rating || 0) >= 3 ? 'good' :
                (inspection.health_rating || 0) >= 2 ? 'warning' : 'danger') as 'excellent' | 'good' | 'warning' | 'danger' | 'neutral',
        icon: '🏥',
        subtitle: 'Overall colony health assessment'
      },
      {
        label: 'Temperament',
        value: inspection.temperament || 'Not Recorded',
        status: (inspection.temperament === 'Calm' ? 'excellent' : 
                inspection.temperament === 'Agitated' ? 'warning' : 
                inspection.temperament === 'Aggressive' ? 'danger' : 'neutral') as 'excellent' | 'good' | 'warning' | 'danger' | 'neutral',
        icon: inspection.temperament === 'Calm' ? '😌' : 
              inspection.temperament === 'Agitated' ? '😠' : 
              inspection.temperament === 'Aggressive' ? '😡' : '🐝',
        subtitle: 'Bee behavior during inspection'
      },
      {
        label: 'Varroa Check',
        value: inspection.varroa_check ? 'Performed' : 'Not Performed',
        status: (inspection.varroa_check ? 'good' : 'warning') as 'excellent' | 'good' | 'warning' | 'danger' | 'neutral',
        icon: '🔬',
        subtitle: inspection.varroa_check ? 'Mite inspection completed' : 'Mite check recommended'
      }
    ];

    // Create metrics based on inspection data
    const healthRating = inspection.health_rating || 1;
    const metrics = {
      overallHealth: Math.min(healthRating * 20, 100),
      populationStrength: Math.min((healthRating * 15) + (inspection.queen_sighted ? 25 : 0), 100),
      broodQuality: inspection.queen_sighted ? Math.min(healthRating * 20 + 20, 100) : 30,
      diseaseRisk: inspection.notes?.toLowerCase().includes('disease') ? 70 : 
                   inspection.varroa_check ? 20 : 40,
      honeyStores: Math.min(healthRating * 15 + 25, 100)
    };

    return {
      date: inspection.date,
      hiveId: hive.id,
      hiveName: hive.name,
      badges,
      metrics,
      notes: inspection.notes,
      weather
    };
  };

  const filteredInspections = getFilteredInspections();

  if (loading) {
    return (
      <div className="enhanced-inspections-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading inspection data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="enhanced-inspections-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-container">
          <div className="hero-header">
            <div className="hero-title">
              <h1>Enhanced Hive Inspections</h1>
              <p className="hero-subtitle">
                Professional in-field inspection workflow with AI-powered analysis and comprehensive reporting
              </p>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon">📊</div>
              <div className="stat-content">
                <div className="stat-number">{stats.totalInspections}</div>
                <div className="stat-label">Total Inspections</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">📅</div>
              <div className="stat-content">
                <div className="stat-number">{stats.thisMonth}</div>
                <div className="stat-label">This Month</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">⏱️</div>
              <div className="stat-content">
                <div className="stat-number">{stats.avgFrequency} days</div>
                <div className="stat-label">Avg Frequency</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">💚</div>
              <div className="stat-content">
                <div className="stat-number">{stats.healthScore}%</div>
                <div className="stat-label">Health Score</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Filters Section */}
      <section className="filters-section">
        <div className="section-header">
          <h2 className="section-title">Filter & Search</h2>
        </div>
        <div className="filter-controls">
          <div className="search-input">
            <span>🔍</span>
            <input
              type="text"
              placeholder="Search inspections..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select
            className="filter-select"
            value={selectedApiaryFilter}
            onChange={(e) => setSelectedApiaryFilter(e.target.value)}
          >
            <option value="">All Apiaries</option>
            {apiaries.map(apiary => (
              <option key={apiary.id} value={apiary.id}>{apiary.name}</option>
            ))}
          </select>
          <select
            className="filter-select"
            value={selectedHiveFilter}
            onChange={(e) => setSelectedHiveFilter(e.target.value)}
          >
            <option value="">All Hives</option>
            {hives.map(hive => (
              <option key={hive.id} value={hive.id}>{hive.name}</option>
            ))}
          </select>
          <select
            className="filter-select"
            value={selectedDateRange}
            onChange={(e) => setSelectedDateRange(e.target.value)}
          >
            <option value="all">All Time</option>
            <option value="week">Last Week</option>
            <option value="month">Last Month</option>
            <option value="quarter">Last Quarter</option>
          </select>
        </div>
      </section>

      {/* Apiaries Section */}
      <section className="apiaries-section">
        <div className="section-header">
          <h2 className="section-title">Quick Inspect by Apiary</h2>
          <span className="section-count">Showing {apiaries.length} apiaries</span>
        </div>
        <div className="apiaries-grid">
          {apiaries.map((apiary) => {
            const apiaryHives = hives.filter(h => h.apiary_id === apiary.id);
            const recentInspections = inspections.filter(i => {
              const hive = hives.find(h => h.id === i.hive_id);
              return hive?.apiary_id === apiary.id;
            }).length;

            return (
              <div key={apiary.id} className="apiary-card">
                <div className="apiary-name">{apiary.name}</div>
                <div className="apiary-details">
                  <div className="apiary-detail">
                    <span className="detail-icon">🏺</span>
                    <span>{apiaryHives.length} hives</span>
                  </div>
                  <div className="apiary-detail">
                    <span className="detail-icon">📋</span>
                    <span>{recentInspections} inspections</span>
                  </div>
                  <div className="apiary-detail">
                    <span className="detail-icon">📍</span>
                    <span>{apiary.location}</span>
                  </div>
                </div>
                <button 
                  className="apiary-inspect-btn"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('🔴 Inspect Apiary button CLICKED for:', apiary.name);
                    handleInspectApiary(apiary);
                  }}
                >
                  <span>🔍</span>
                  <span>Inspect Apiary</span>
                </button>
              </div>
            );
          })}
        </div>
      </section>

      {/* Recent Inspections Section */}
      <section className="inspections-section">
        <div className="section-header">
          <h2 className="section-title">Recent Inspections</h2>
          <span className="section-count">Showing {filteredInspections.length} inspections</span>
        </div>

        {filteredInspections.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📋</div>
            <h3>No inspections found</h3>
            <p>
              {inspections.length === 0 
                ? "Start by conducting your first hive inspection!" 
                : "Try adjusting your filters to see more results."
              }
            </p>
            <button className="start-inspection-btn" onClick={handleStartNewInspection}>
              <span className="icon">📋</span>
              <span>Start New Inspection</span>
            </button>
          </div>
        ) : (
          <div className="inspections-grid">
            {filteredInspections.slice(0, 10).map((inspection) => {
              const metrics = getInspectionMetrics(inspection);
              
              return (
                <div key={inspection.id} className="inspection-card">
                  <div className="inspection-header">
                    <div className="inspection-header-top">
                      <div className="inspection-hive">{getHiveName(inspection.hive_id)}</div>
                      <span className="inspection-date">
                        📅 {new Date(inspection.date).toLocaleDateString('en-GB')}
                      </span>
                    </div>
                    <div className="inspection-header-bottom">
                      <span className="inspection-badge">{getApiaryName(inspection.hive_id)}</span>
                    </div>
                  </div>
                  <div className="inspection-body">
                    <div className="inspection-metrics">
                      {metrics.map((metric, index) => (
                        <div key={index} className="metric">
                          <div className={`metric-icon ${metric.status}`}>{metric.icon}</div>
                          <div className="metric-label">{metric.label}</div>
                        </div>
                      ))}
                    </div>
                    {inspection.notes && (
                      <div className="inspection-notes">
                        <div className="notes-label">Quick Notes:</div>
                        <div className="notes-text">
                          {inspection.notes.length > 100 
                            ? `${inspection.notes.substring(0, 100)}...` 
                            : inspection.notes
                          }
                        </div>
                      </div>
                    )}
                    <div className="inspection-actions">
                      <button 
                        className="inspection-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log('🔴 View Details button CLICKED for inspection:', inspection.id);
                          handleViewInspectionDetails(inspection);
                        }}
                      >
                        <span>📄</span>
                        <span>View Details</span>
                      </button>
                      <button 
                        className="inspection-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log('✏️ Edit button CLICKED for inspection:', inspection.id);
                          handleEditInspection(inspection);
                        }}
                      >
                        <span>✏️</span>
                        <span>Edit</span>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Hive Selection Modal */}
      {showHiveSelection && selectedApiary && (
        <HiveSelection
          selectedApiary={selectedApiary}
          onHiveSelect={handleHiveSelect}
          onCancel={handleCloseHiveSelection}
          onAddNewHive={() => {}} // Could implement this later
        />
      )}

      {/* Inspection Form Modal */}
      {showInspectionForm && selectedHive && selectedApiary && (
        <div style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          overflow: 'auto'
        }}>
          <div style={{
            position: 'relative',
            maxHeight: '90vh',
            overflow: 'auto',
            margin: '20px'
          }}>
            <EnhancedInspectionForm
              selectedApiary={selectedApiary}
              selectedHive={selectedHive}
              onClose={handleCloseInspectionForm}
              onSave={loadData}
            />
          </div>
        </div>
      )}

      {/* Inspection Report Modal */}
      {showInspectionReport && viewingInspection && selectedHive && selectedApiary && (
        <div style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          overflow: 'auto'
        }}>
          <div style={{
            position: 'relative',
            maxHeight: '90vh',
            maxWidth: '90vw',
            overflow: 'auto',
            margin: '20px',
            backgroundColor: 'white',
            borderRadius: '12px'
          }}>
            {/* Close button */}
            <button
              onClick={handleCloseInspectionReport}
              style={{
                position: 'absolute',
                top: '15px',
                right: '15px',
                background: 'rgba(0, 0, 0, 0.1)',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontSize: '18px',
                zIndex: 1001
              }}
            >
              ✕
            </button>
            <EnhancedInspectionReport
              {...transformInspectionForReport(viewingInspection, selectedHive, selectedApiary)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedInspectionsPage;